/**
 * @fileoverview What to plant's search (E24): sends what she typed, a moment after she stops typing, and hands back the
 * server's answer, the rows to draw in order and in groups with the closing line (bloom-backend
 * services/plants/whatToPlantSearch decides every row). Nothing is matched or filtered here.
 *
 * Where: CropSearch (CropSearchResults), on What to plant and on See them.
 * API: GET /plants/what-to-plant/search?q=&lang= (api/whatToPlant getWhatToPlantSearch).
 * Caching: keyed by the garden, its own day, the language and the text sent; the last answer stays drawn while the next
 * is on its way; an add marks it stale without reading it again on the page (useAddCrop).
 *
 * @module useWhatToPlantSearch
 */

import React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/hooks/useAuth';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getWhatToPlantSearch, type WhatToPlantSearch } from '@/shared/api/whatToPlant';
import { chosenGarden } from '@/shared/api/gardenMembers';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';

/** The first word of the search's React Query key. @kept K1 */
export const WHAT_TO_PLANT_SEARCH_QUERY_KEY = 'what-to-plant-search';

/** How long typing must pause before the text is sent, in milliseconds. @kept K1 */
export const SEARCH_PAUSE_MS = 250;

/** What the results are handed. */
export interface WhatToPlantSearchReading {
  /** The server's answer for the text last sent, or undefined until it is in. */
  data: WhatToPlantSearch | undefined;
  /** True while the first answer is on its way (a later one replaces the last answer when it comes). */
  isLoading: boolean;
  /** The failure, or null. */
  error: unknown;
}

/**
 * The text as it stands once typing has paused for SEARCH_PAUSE_MS (the first text at once, so a page opened on ?q=
 * asks straight away). The Add a plant search (usePlantSearch, B121) pauses the same way.
 *
 * @param text - What is typed now.
 * @returns The text to send.
 */
export function usePaused(text: string): string {
  const [paused, setPaused] = React.useState(text);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setPaused(text), SEARCH_PAUSE_MS);
    return () => window.clearTimeout(timer);
  }, [text]);
  return paused;
}

/**
 * Asks the server which rows answer what she typed.
 *
 * @param query - What she typed (trimmed here; nothing is asked for an empty search).
 * @returns The answer, whether it is on its way, and any failure.
 */
export function useWhatToPlantSearch(query: string): WhatToPlantSearchReading {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  const { date, known } = useGardenPostcode();
  const gardenId = chosenGarden() ?? mysqlUser?.user_id ?? null;
  const sent = usePaused(query.trim());
  const { data, isLoading, error } = useQuery({
    queryKey: [WHAT_TO_PLANT_SEARCH_QUERY_KEY, gardenId, date, language, sent],
    queryFn: () => getWhatToPlantSearch(sent, language),
    enabled: Boolean(mysqlUser) && known && sent.length > 0,
    staleTime: QUERY_STALE_TIME_MS,
    // While she types on, the last answer stays drawn until the next one is in.
    placeholderData: keepPreviousData,
  });
  return { data, isLoading: isLoading || !known, error: error ?? null };
}
